import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm, FormProvider } from 'react-hook-form'
import { useModifyComponentBlueprintDefinition } from '../../../../../api/denali'
import OpalCard from '../../../../opal/OpalCard'
import InlineCardHeader from '../../../../molecules/Card/InlineCardHeader'
import SydButton from '../../../../commonDesign/Button'
import {
  ComponentListSidebar,
  ComponentDetailPanel,
  AddComponentDialog
} from './componentManagement'

function backfillOrdinals (presentations) {
  const entries = Object.entries(presentations || {})
  const needsBackfill = entries.some(([, v]) => v.ordinal === undefined || v.ordinal === null)
  if (!needsBackfill) return presentations || {}
  const result = {}
  entries.forEach(([code, data], idx) => {
    result[code] = { ...data, ordinal: data.ordinal ?? idx }
  })
  return result
}

function ComponentsTab ({ blueprint, onSave }) {
  const { mutateAsync: modifyDefinition } = useModifyComponentBlueprintDefinition()
  const addComponentRef = useRef(null)

  const serverPresentations = useMemo(
    () => backfillOrdinals(blueprint.definition?.data?.presentations),
    [blueprint.definition]
  )

  const form = useForm({
    defaultValues: { presentations: serverPresentations }
  })

  const { setValue, getValues, reset, watch } = form

  const presentations = watch('presentations')

  const [selectedCode, setSelectedCode] = useState(() => {
    const codes = Object.keys(serverPresentations)
    return codes[0] || null
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const updated = backfillOrdinals(blueprint.definition?.data?.presentations)
    reset({ presentations: updated })
    setSelectedCode(prev => {
      if (prev && updated[prev]) return prev
      const sorted = Object.entries(updated).sort(([, a], [, b]) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
      return sorted[0]?.[0] || null
    })
  }, [blueprint.definition, reset])

  const handleAddComponent = useCallback(({ code, data }) => {
    const current = getValues('presentations') || {}
    const maxOrdinal = Object.values(current).reduce(
      (max, p) => Math.max(max, p.ordinal ?? 0), -1
    )
    setValue('presentations', {
      ...current,
      [code]: { ...data, ordinal: maxOrdinal + 1 }
    }, { shouldDirty: true })
    setSelectedCode(code)
  }, [getValues, setValue])

  const handleRemoveComponent = useCallback((code) => {
    const current = getValues('presentations') || {}
    const entry = current[code]
    const confirmed = window.confirm(`Remove component "${entry?.displayName || code}"?`)
    if (!confirmed) return

    const { [code]: _, ...remaining } = current
    reset({ presentations: remaining }, { keepDefaultValues: true })

    if (selectedCode === code) {
      const sorted = Object.entries(remaining)
        .sort(([, a], [, b]) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
      setSelectedCode(sorted[0]?.[0] || null)
    }
  }, [getValues, reset, selectedCode])

  const handleReorder = useCallback((dragIndex, hoverIndex) => {
    const current = getValues('presentations') || {}
    const sorted = Object.entries(current)
      .sort(([, a], [, b]) => (a.ordinal ?? 0) - (b.ordinal ?? 0))

    const reordered = [...sorted]
    const [moved] = reordered.splice(dragIndex, 1)
    reordered.splice(hoverIndex, 0, moved)

    const updated = {}
    reordered.forEach(([code, data], idx) => {
      updated[code] = { ...data, ordinal: idx }
    })
    setValue('presentations', updated, { shouldDirty: true })
  }, [getValues, setValue])

  const handleReset = useCallback(() => {
    const updated = backfillOrdinals(blueprint.definition?.data?.presentations)
    reset({ presentations: updated })
    setSelectedCode(prev => {
      if (prev && updated[prev]) return prev
      const sorted = Object.entries(updated).sort(([, a], [, b]) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
      return sorted[0]?.[0] || null
    })
  }, [blueprint.definition, reset])

  const handleSave = useCallback(async () => {
    try {
      setSaving(true)
      const current = getValues('presentations')
      await modifyDefinition({
        blueprintId: blueprint.blueprintId,
        data: { presentations: current }
      })
      reset({ presentations: current })
      onSave()
    } catch (err) {
      console.error('Failed to save components:', err)
    } finally {
      setSaving(false)
    }
  }, [blueprint.blueprintId, getValues, modifyDefinition, onSave, reset])

  const fieldPrefix = selectedCode ? `presentations.${selectedCode}` : null

  return (
    <FormProvider {...form}>
      <OpalCard size='sm'>
        <InlineCardHeader
          action={
            <div style={{ display: 'flex', gap: '8px' }}>
              <SydButton
                onClick={handleReset}
                variant='ghost'
                size='sm'
                icon='refresh'
                disabled={saving}
              >
                Reset
              </SydButton>
              <SydButton
                onClick={handleSave}
                processing={saving}
                variant='primary'
                size='sm'
                icon='check'
              >
                Save Changes
              </SydButton>
            </div>
          }
        >
          Components
        </InlineCardHeader>
        <div style={{ padding: '16px' }}>
          {blueprint.definition ? (
            <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '0', minHeight: '400px' }}>
              <ComponentListSidebar
                presentations={presentations}
                selectedCode={selectedCode}
                onSelect={setSelectedCode}
                onAdd={() => addComponentRef.current.open()}
                onRemove={handleRemoveComponent}
                onReorder={handleReorder}
              />
              {selectedCode && fieldPrefix ? (
                <ComponentDetailPanel
                  key={selectedCode}
                  code={selectedCode}
                  fieldPrefix={fieldPrefix}
                />
              ) : (
                <div style={{
                  padding: '32px',
                  textAlign: 'center',
                  color: 'var(--text-color-subtle)',
                  paddingLeft: '32px'
                }}
                >
                  <p>Select a component to view and edit details</p>
                </div>
              )}
            </div>
          ) : (
            <p style={{ color: 'var(--color-neutral-500)' }}>
              No active definition. Add a version from the Settings tab to get started.
            </p>
          )}
        </div>

        <AddComponentDialog ref={addComponentRef} onComplete={handleAddComponent} />
      </OpalCard>
    </FormProvider>
  )
}

ComponentsTab.propTypes = {
  blueprint: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired
}

export default ComponentsTab
